import { Participant } from '@wix/ambassador-challenges-v1-participant/types';

import { Challenge } from '@wix/ambassador-challenges-v1-challenge/types';

import { getSelfPacedSettings } from '../../../../selectors/challenges';
import isSameDay from 'date-fns/isSameDay';
import flatten from 'lodash/flatten';

import { getRightDateFromBackend } from '../../../../selectors/dates';
import { ISettingsContextValue } from '@wix/tpa-settings';
import challengeSettings from '../../settingsParams';
import { isAfter } from 'date-fns';
import { useParticipantSteps } from '../../../../contexts/ParticipantStepsDataProvider/ParticipantStepsContext';
import { useSections } from '../../../../contexts/ParticipantSections/ParticipantSectionsContext';
import { isFlexible } from '../../../../selectors/isFlexible';
import { isSpecific } from '../../../../selectors/isSpecific';
import { isSelfPaced } from '../../../../selectors/isSelfPaced';
import { isUserCompleted } from '../../../../contexts/User/helpers/userTypeHandlers';
import { TranslateFunction } from '@wix/challenges-web-library';
import { getBidirectionalAlignment } from '../../../../utils/GetBidirectionalAlignment';
import { getParticipantsNumber } from '../../../../selectors/programs/getParticipantsNumber';
import {
  ParticipantSection,
  ParticipantStep,
  ParticipantStepState,
} from '../../../../types/v3Types';
import { getQuizId } from '../../../../selectors/step';
import {
  isSectionLocked,
  getWaitingStart,
} from '../../../../selectors/sections';

const STEP_TOOLTIP_KEY = {
  [ParticipantStepState.COMPLETED]: 'step.tooltip.completed',
  [ParticipantStepState.OVERDUE]: 'step.tooltip.missed',
  [ParticipantStepState.PENDING]: 'step.tooltip.pending-scheduled',
  [ParticipantStepState.RUNNING]: 'step.tooltip.running',
  'PENDING-SPC': 'step.tooltip.pending-spc',
  'PENDING-SCHEDULED': 'step.tooltip.pending-scheduled',
  STEP_COMPLETION_DISABLED: 'step.tooltip.complete-not-allowed',
};

export enum ChallengeEntityType {
  STEP = 'STEP',
  VOID = 'VOID',
}

export interface IFollowingEntity {
  step?: ParticipantStep;
  type: ChallengeEntityType;
  id?: string;
}

export interface ISiteColor {
  name: string;
  value: string;
  reference: string;
}

const getBaseViewOptions = (challenge: Challenge) => {
  const showHeaderDate = isSpecific(challenge);

  const stepsCount = challenge?.stepsSummary?.stepsNumber;
  const showSteps = !!stepsCount;
  const showDuration = Boolean(
    isFlexible(challenge) ||
      isSpecific(challenge) ||
      (isSelfPaced(challenge) && getSelfPacedSettings(challenge)?.duration),
  );
  const showParticipants = !!getParticipantsNumber(challenge);

  const overviewItemsCount =
    Number(showSteps) + Number(showDuration) + Number(showParticipants);

  return {
    showHeaderDate,
    showSteps,
    stepsCount,
    showDuration,
    showParticipants,
    overviewItemsCount,
  };
};

const getBaseViewOptionsForVisitor = (
  challenge: Challenge,
  settings: ISettingsContextValue,
  cssPBEnabled: boolean = false,
) => {
  const {
    showHeaderDate,
    showSteps,
    stepsCount,
    showDuration,
    showParticipants,
    overviewItemsCount,
  } = getBaseViewOptions(challenge);

  const showStepsForVisitor =
    showSteps &&
    (cssPBEnabled || settings.get(challengeSettings.displayChallengeSteps));
  const showDurationForVisitor =
    showDuration &&
    (cssPBEnabled || settings.get(challengeSettings.displayChallengeDuration));
  const showParticipantsForVisitor =
    showParticipants &&
    (cssPBEnabled ||
      settings.get(challengeSettings.displayChallengeParticipants));

  return {
    showHeaderDate,
    stepsCount,
    showSteps: showStepsForVisitor,
    showDuration: showDurationForVisitor,
    showParticipants: showParticipantsForVisitor,
    showOverview:
      showStepsForVisitor ||
      showDurationForVisitor ||
      showParticipantsForVisitor,
    overviewItemsCount,
  };
};

const getBaseViewOptionsForSidebar = (
  challenge: Challenge,
  settings: ISettingsContextValue,
  cssPBEnabled: boolean = false,
) => {
  const {
    showHeaderDate,
    showSteps,
    stepsCount,
    showDuration,
    showParticipants,
    overviewItemsCount,
  } = getBaseViewOptions(challenge);

  return {
    showHeaderDate,
    showSteps:
      showSteps &&
      (cssPBEnabled ||
        settings.get(challengeSettings.sidebarLayoutDisplayChallengeSteps)),
    stepsCount,
    showDuration:
      showDuration &&
      (cssPBEnabled ||
        settings.get(challengeSettings.sidebarLayoutDisplayChallengeDuration)),
    showParticipants:
      showParticipants &&
      (cssPBEnabled ||
        settings.get(
          challengeSettings.sidebarLayoutDisplayChallengeParticipants,
        )),
    overviewItemsCount,
  };
};

const getResolveStepData = (
  currentStep: ParticipantStep,
  formQuizData: { isFormQuiz: boolean; quizSubmissionId: string } = {
    isFormQuiz: false,
    quizSubmissionId: null,
  },
) => {
  const _isStepResolved = isStepResolved(currentStep);
  const currentStepAfterUpdate: ParticipantStep = { ...currentStep };

  if (!_isStepResolved) {
    currentStepAfterUpdate.transitions = [
      {
        state: ParticipantStepState.COMPLETED,
        occurredAt: new Date(),
      },
    ];
  }

  if (formQuizData?.isFormQuiz) {
    currentStepAfterUpdate.quizSubmission = {
      quizSubmissionId: formQuizData?.quizSubmissionId,
      score: 100, // for the future
    };
  }

  return {
    isStepResolved: _isStepResolved,
    currentStepAfterUpdate,
  };
};

const getStepsResolvedValue = (steps: ParticipantStep[]) => {
  return Math.floor(
    (steps.reduce(
      ((resolved: number, step: ParticipantStep) => {
        return isStepResolved(step) ? ++resolved : resolved;
      }) as any,
      0,
    ) /
      (steps.length || 1)) *
      100,
  );
};

export const getNextStepToComplete = (
  flatStepsList: ParticipantStep[] = [],
): ParticipantStep => {
  return (
    getFirstAvailableStep(flatStepsList) ||
    flatStepsList.find((step) => {
      return utils.isStepLocked(step);
    })
  );
};

export const getFirstAvailableStep = (
  flatStepsList: ParticipantStep[] = [],
): ParticipantStep => {
  return (
    flatStepsList.find((step) => {
      return isStepAvailableForComplete(step);
    }) ||
    flatStepsList.find((step) => {
      return isStepOverdue(step);
    }) ||
    getLastCompletedStep(flatStepsList)
  );
};

const getLastCompletedStep = (
  flatStepsList: ParticipantStep[] = [],
): ParticipantStep => {
  return flatStepsList
    .slice(0)
    .reverse()
    .find((step) => {
      return isStepResolved(step);
    });
};

export const isStepHiddenOptimized = (
  program: Challenge,
  step: ParticipantStep,
  firstAvailableStep: ParticipantStep,
) => {
  const isSPC = isSelfPaced(program);
  const isResolveStepsInOrderSettingEnabled =
    !!program?.settings?.accessRestrictions?.resolveStepsInOrder;
  const isHideFutureStepsSettingEnabled =
    !!program?.settings?.accessRestrictions?.hideFutureSteps;
  const _isStepResolved = isStepResolved(step);

  const isSPCStepFirstAvailable = firstAvailableStep?.id === step?.id;
  const isScheduledStepInTheFuture = isAfter(
    getRightDateFromBackend(step?.dateFrame?.start),
    new Date(),
  );

  const isStepQuizSubmitted = step?.quizSubmission?.quizSubmissionId;
  const formId = getQuizId(step?.source);
  const isQuiz = !!formId;

  if (isQuiz && isStepQuizSubmitted) {
    return false;
  }

  const isRequirementsForHideStepComplied = !_isStepResolved;

  return isSPC
    ? isRequirementsForHideStepComplied &&
        !isSPCStepFirstAvailable &&
        isResolveStepsInOrderSettingEnabled &&
        isHideFutureStepsSettingEnabled
    : isRequirementsForHideStepComplied &&
        isHideFutureStepsSettingEnabled &&
        isScheduledStepInTheFuture;
};

const isStepHidden = (
  challenge: Challenge,
  flatStepsList: ParticipantStep[], // should be sorted properly for the SPC
  step: ParticipantStep,
  firstAvailableStep = getFirstAvailableStep(flatStepsList),
) => {
  return isStepHiddenOptimized(challenge, step, firstAvailableStep);
};

export const isVisibleStepLockedForCompleteOptimized = (
  challenge: Challenge,
  step: ParticipantStep,
  firstAvailableStep: ParticipantStep,
) => {
  const isSPC = isSelfPaced(challenge);
  const isResolveStepsInOrderSettingEnabled =
    !!challenge?.settings?.accessRestrictions?.resolveStepsInOrder;
  const isHideFutureStepsSettingEnabled =
    !!challenge?.settings?.accessRestrictions?.hideFutureSteps;
  const _isStepResolved = isStepResolved(step);

  const isSPCStepFirstAvailable = firstAvailableStep?.id === step?.id;

  /*
    For SPC we can set `resolve steps in order` but without hiding further steps.
   */

  return (
    !_isStepResolved &&
    isSPC &&
    !isSPCStepFirstAvailable &&
    isResolveStepsInOrderSettingEnabled &&
    !isHideFutureStepsSettingEnabled
  );
};

const isVisibleStepLockedForComplete = (
  challenge: Challenge,
  flatStepsList: ParticipantStep[], // should be sorted properly for the SPC
  step: ParticipantStep,
  firstAvailableStep = getFirstAvailableStep(flatStepsList),
) => {
  return isVisibleStepLockedForCompleteOptimized(
    challenge,
    step,
    firstAvailableStep,
  );
};

export const isSectionLockedForStep = (
  currentStep: ParticipantStep | null,
  listParticipantSections: ParticipantSection[] | undefined,
) => {
  const currentSection = currentStep
    ? listParticipantSections?.find((section) => 
        section.steps?.some((step) => step.id === currentStep.id),
      )
    : null;

  return {
    isSectionLocked: !!currentSection && isSectionLocked(currentSection),
    waitingStart: currentSection ? getWaitingStart(currentSection) : undefined,
  };
};

export type ChallengeStepData = {
  listParticipantSections: ParticipantSection[];
  participantSteps: { steps: ParticipantStep[] };
};

export const isStepFailed = (step: ParticipantStep): boolean => {
  return step.transitions?.['0']?.state === ParticipantStepState.FAILED;
};

type NextButtonLabel = {
  entity: ChallengeEntityType;
  t: TranslateFunction;
  nextButtonTxt: string;
};

export function getNextButtonLabel(options: NextButtonLabel): string {
  const { entity, nextButtonTxt } = options;

  switch (entity) {
    case ChallengeEntityType.STEP:
      return nextButtonTxt;
    case ChallengeEntityType.VOID:
      return '';
  }
}

export function isQuizFailed(step: ParticipantStep) {
  return step?.quizSubmission?.quizSubmissionId && isStepFailed(step);
}

const utils = {
  getBaseViewOptions,
  isStepFailed,
  getStepTooltipKey: (
    step: ParticipantStep,
    isUnavailable: boolean,
    isSPC: boolean,
    participant: Participant,
  ) => {
    if (
      (isStepOverdue(step) || isStepPending(step) || isStepRunning(step)) &&
      isUserCompleted(participant?.transitions?.[0]?.state)
    ) {
      return STEP_TOOLTIP_KEY.STEP_COMPLETION_DISABLED;
    }

    if (isUnavailable) {
      return isSPC
        ? STEP_TOOLTIP_KEY['PENDING-SPC']
        : STEP_TOOLTIP_KEY['PENDING-SCHEDULED'];
    }

    if (isStepPending(step)) {
      // eslint-disable-next-line no-nested-ternary
      return isSameDay(new Date(step?.dateFrame?.start), new Date())
        ? STEP_TOOLTIP_KEY.RUNNING
        : isSPC
        ? STEP_TOOLTIP_KEY['PENDING-SPC']
        : STEP_TOOLTIP_KEY['PENDING-SCHEDULED'];
    }

    return STEP_TOOLTIP_KEY[step.transitions[0].state];
  },

  getBaseViewOptionsForVisitor,
  getBaseViewOptionsForSidebar,

  isStepLocked: (step: ParticipantStep) => {
    return !isStepAvailableForComplete(step) && isStepPending(step);
  },
  getResolveStepData,
  getFollowingEntity(
    stepId: string | null,
    data: ChallengeStepData,
    type: 'previous' | 'next' = 'next',
  ): IFollowingEntity {
    const followingStep = utils.getFollowingStep(
      stepId,
      getFlatStepsList(data),
      type,
    );

    if (followingStep) {
      return {
        step: followingStep,
        type: ChallengeEntityType.STEP,
        id: followingStep.id,
      };
    }

    return {
      type: ChallengeEntityType.VOID,
    };
  },

  getFollowingStep(
    stepId: string,
    steps: ParticipantStep[] = [],
    type: 'previous' | 'next' = 'next',
  ) {
    const dir = type === 'next' ? 1 : -1;
    const currentStepInd = steps.findIndex((step) => step.id === stepId);

    return steps[currentStepInd + dir] || null;
  },

  getStepsResolvedValue,

  scrollToChallengePage: () => {
    const challengePageDataHook = 'challenge-page';

    document
      .querySelector(`[data-hook="${challengePageDataHook}"]`)
      ?.scrollIntoView({
        behavior: 'smooth',
      });
  },

  getHeaderAlignByType: (settings: ISettingsContextValue, isRTL?: boolean) => {
    return getBidirectionalAlignment(
      settings.get(challengeSettings.sidebarLayoutTextAlignment),
      isRTL,
    );
  },

  getContentAlignByType: (settings: ISettingsContextValue, isRTL = false) => {
    return getBidirectionalAlignment(
      settings.get(challengeSettings.sidebarLayoutTextAlignment),
      isRTL,
    );
  },

  getFirstAvailableStep,
  getLastCompletedStep,
  isStepHidden,
  isVisibleStepLockedForComplete,

  getPassingGrade: (step: ParticipantStep, isFilled: boolean) => {
    return isFilled
      ? utils.getParticipantPassingGrade(step)
      : utils.getOwnerPassingGrade(step);
  },

  getOwnerPassingGrade: (step: ParticipantStep) => {
    return step?.quizSubmission?.passingGrade;
  },

  getParticipantPassingGrade: (step: ParticipantStep) => {
    return step?.quizSubmission?.passingGrade;
  },

  wait: async () => {
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  },
};
export const isStepResolved = (step: ParticipantStep): boolean => {
  return step?.transitions?.['0']?.state === ParticipantStepState.COMPLETED;
};

export const getFlatStepsList = (data: ChallengeStepData) => {
  // both for the sections and without them
  const {
    listParticipantSections = [],
    participantSteps: { steps = [] } = {},
  } = data;
  const isSections = listParticipantSections.length;

  return isSections
    ? flatten(listParticipantSections.map((section) => section.steps || []))
    : steps;
};

export const isStepOverdue = (step: ParticipantStep): boolean => {
  return step?.transitions?.['0']?.state === ParticipantStepState.OVERDUE;
};

export const isStepRunning = (step: ParticipantStep): boolean => {
  return step?.transitions?.['0']?.state === ParticipantStepState.RUNNING;
};

export const isStepPending = (step: ParticipantStep): boolean => {
  return step?.transitions?.['0']?.state === ParticipantStepState.PENDING;
};

export const isStepAvailableForComplete = (step: ParticipantStep) => {
  return (
    step?.transitions?.['0'] &&
    (isStepRunning(step) ||
      isStepFailed(step) ||
      (isStepPending(step) &&
        isSameDay(getRightDateFromBackend(step.dateFrame.start), new Date())))
  );
};

export const useNextEntity = (stepId: string) => {
  const { listParticipantSections } = useSections();
  const { participantSteps } = useParticipantSteps();

  return {
    nextEntity: utils.getFollowingEntity(stepId, {
      listParticipantSections,
      participantSteps,
    }),
  };
};

export default utils;

export function getProgramProgress(flatStepsList: ParticipantStep[]) {
  const progressPercentage = utils.getStepsResolvedValue(flatStepsList);
  const isProgramNotStarted = progressPercentage === 0;
  const isProgramFinished = progressPercentage === 100;

  return { progressPercentage, isProgramNotStarted, isProgramFinished };
}
